import React, { useState } from 'react';
import { X, KeyRound, Loader2, Eye, EyeOff, Copy, RefreshCw, ShieldCheck } from 'lucide-react';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';

function genPassword(length = 16) {
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const lower = 'abcdefghijkmnpqrstuvwxyz';
    const digits = '23456789';
    const symbols = '!@#$%&*_-+=';
    const all = upper + lower + digits + symbols;
    let arr = [
        upper[Math.floor(Math.random() * upper.length)],
        lower[Math.floor(Math.random() * lower.length)],
        digits[Math.floor(Math.random() * digits.length)],
        symbols[Math.floor(Math.random() * symbols.length)],
    ];
    for (let i = 0; i < length - 4; i++) arr.push(all[Math.floor(Math.random() * all.length)]);
    return arr.sort(() => Math.random() - 0.5).join('');
}

export default function ResetPasswordModal({ userId, userLabel, accent = '#7C6BEF', onClose }) {
    const [password, setPassword] = useState(() => genPassword());
    const [show, setShow] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const regen = () => setPassword(genPassword());

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(password);
            toast.success('Contraseña copiada al portapapeles');
        } catch {
            toast.error('No se pudo copiar. Cópiala manualmente.');
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 10) {
            toast.error('La contraseña debe tener al menos 10 caracteres'); return;
        }
        setSubmitting(true);
        try {
            await api.post(`/admin/users/${userId}/reset-password`, { new_password: password });
            toast.success('Contraseña restablecida. El usuario deberá iniciar sesión de nuevo.');
            onClose();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Error al resetear contraseña'));
        } finally { setSubmitting(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose}>
            <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
                className="w-full max-w-lg max-h-[calc(100vh-2rem)] overflow-y-auto" data-testid="reset-password-modal"
                style={{ background: 'var(--card-bg, #fff)', border: '1px solid var(--border, #E5E7EB)' }}>
                <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--border, #E5E7EB)' }}>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center" style={{ background: `${accent}18`, color: accent }}>
                            <KeyRound className="w-5 h-5" />
                        </div>
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: accent }}>Reset seguro</div>
                            <h2 className="font-display text-xl font-black">Restablecer contraseña</h2>
                        </div>
                    </div>
                    <button type="button" onClick={onClose} className="p-2 hover:bg-black/5 dark:hover:bg-white/10"><X className="w-5 h-5" /></button>
                </div>

                <div className="p-6 space-y-4">
                    <div className="text-sm text-gray-600">
                        Se generará una nueva contraseña para <span className="font-bold">{userLabel}</span>.
                        Todas las sesiones activas del usuario se cerrarán y deberá iniciar sesión con la nueva contraseña.
                    </div>

                    <div>
                        <label className="text-xs uppercase tracking-wider font-bold mb-2 block text-gray-500">Nueva contraseña</label>
                        <div className="relative">
                            <input type={show ? 'text' : 'password'} value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="reset-password-input"
                                className="w-full px-3 py-2.5 pr-24 border outline-none font-mono text-sm"
                                style={{ borderColor: 'var(--border, #E5E7EB)' }} />
                            <div className="absolute right-1 top-1/2 -translate-y-1/2 flex gap-0.5">
                                <button type="button" onClick={() => setShow(!show)} className="p-1.5 hover:bg-gray-100" title={show ? 'Ocultar' : 'Mostrar'}>
                                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                                <button type="button" onClick={copy} className="p-1.5 hover:bg-gray-100" title="Copiar">
                                    <Copy className="w-4 h-4" />
                                </button>
                                <button type="button" onClick={regen} className="p-1.5 hover:bg-gray-100" title="Regenerar">
                                    <RefreshCw className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                        <div className="text-xs text-gray-500 mt-2">
                            Guarda o comparte esta contraseña de forma segura con el usuario. No podrás verla de nuevo después de confirmar.
                        </div>
                    </div>

                    <div className="text-xs bg-amber-50 border border-amber-200 p-3 text-amber-900 flex items-start gap-2">
                        <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>Se cerrarán todas las sesiones activas del usuario tras el reset.</span>
                    </div>
                </div>

                <div className="px-6 py-4 border-t flex gap-2 justify-end" style={{ borderColor: 'var(--border, #E5E7EB)' }}>
                    <button type="button" onClick={onClose}
                        className="px-5 py-2 border hover:bg-black/5 dark:hover:bg-white/10" style={{ borderColor: 'var(--border, #E5E7EB)' }}>
                        Cancelar
                    </button>
                    <button type="submit" disabled={submitting}
                        data-testid="submit-reset-password"
                        className="text-white px-6 py-2 font-bold inline-flex items-center gap-2 disabled:opacity-50"
                        style={{ background: accent }}>
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                        Confirmar y aplicar
                    </button>
                </div>
            </form>
        </div>
    );
}
